export const GOOGLE_SHEET_IMPORT_TYPES = new Set([
  'google_sheet',
  'sheet_lookup',
  'spreadsheet',
]);

export const NATIVE_SHEET_MIME = 'application/vnd.google-apps.spreadsheet';

export const SHEET_NO_MATCH_DEFAULT =
  'Sorry, we could not find any matching details for that. Please check and try again.';

export const SHEET_FOUND_HANDLE = 'sheet_found';
export const SHEET_NOT_FOUND_HANDLE = 'sheet_not_found';

function optionalStepOrder(value) {
  if (value == null || value === '') return null;
  const order = Number(value);
  return Number.isFinite(order) ? order : null;
}

/** Found path. New field wins. Older flows only stored next_step_order. */
export function sheetFoundNextOrder(step) {
  const explicit = optionalStepOrder(step?.google_sheet_found_next_step_order);
  if (explicit != null) return explicit;
  return optionalStepOrder(step?.next_step_order);
}

export function sheetNotFoundNextOrder(step) {
  const explicit = optionalStepOrder(
    step?.google_sheet_no_match_next_step_order,
  );
  if (explicit != null) return explicit;
  return optionalStepOrder(step?.google_sheet_not_found_next_step_order);
}

const RESERVED_PLACEHOLDERS = new Set(['name', 'brand']);

export function isGoogleSheetImportType(step) {
  const type = String(step?.type || step?.step_type || '')
    .trim()
    .toLowerCase();
  return GOOGLE_SHEET_IMPORT_TYPES.has(type);
}

export function isGoogleSheetStep(step) {
  return !!(step?.is_google_sheet_step || isGoogleSheetImportType(step));
}

/** Header "Unit Price" becomes placeholder name Unit_Price. */
export function headerToPlaceholder(header) {
  const name = String(header || '')
    .trim()
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
  if (!/^[A-Za-z][A-Za-z0-9_]{0,63}$/.test(name)) return '';
  return name;
}

export function placeholderHint(header) {
  const name = headerToPlaceholder(header);
  if (!name) {
    return 'Reply template uses {{Header_Name}}. Spaces and symbols become _. Name must start with a letter.';
  }
  if (RESERVED_PLACEHOLDERS.has(name.toLowerCase())) {
    return `{{${name}}} is the customer or company name, so a sheet column with that name will not fill it.`;
  }
  return `Reply template uses {{${name}}}. {{name}} and {{brand}} stay the customer and company names.`;
}

export function googleSheetFields(step = {}) {
  const on = isGoogleSheetStep(step);
  if (!on) {
    return {
      is_google_sheet_step: false,
      google_sheet_spreadsheet_id: '',
      google_sheet_name: '',
      google_sheet_lookup_column: '',
      google_sheet_lookup_source: 'message',
      google_sheet_lookup_variable: '',
      google_sheet_reply_template: '',
      google_sheet_no_match_message: '',
      google_sheet_found_next_step_order: null,
      google_sheet_no_match_next_step_order: null,
      google_sheet_not_found_next_step_order: null,
    };
  }
  const source =
    step.google_sheet_lookup_source === 'variable' ? 'variable' : 'message';
  return {
    is_google_sheet_step: true,
    google_sheet_spreadsheet_id: String(
      step.google_sheet_spreadsheet_id || '',
    ).trim(),
    google_sheet_name: String(step.google_sheet_name || '').trim() || 'Sheet1',
    google_sheet_lookup_column: String(
      step.google_sheet_lookup_column || '',
    ).trim(),
    google_sheet_lookup_source: source,
    google_sheet_lookup_variable:
      source === 'variable'
        ? String(step.google_sheet_lookup_variable || '').trim()
        : '',
    google_sheet_reply_template: step.google_sheet_reply_template || '',
    google_sheet_no_match_message: step.google_sheet_no_match_message || '',
    google_sheet_found_next_step_order: optionalStepOrder(
      step.google_sheet_found_next_step_order,
    ),
    google_sheet_no_match_next_step_order: sheetNotFoundNextOrder(step),
    google_sheet_not_found_next_step_order: sheetNotFoundNextOrder(step),
  };
}

export function sanitizeVariableName(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '')
    .slice(0, 64);
}

/** Open-ended variables saved on steps that run before `currentOrder`. */
export function priorStepVariables(steps, currentOrder) {
  const order = Number(currentOrder);
  const seen = new Set();
  return (steps || [])
    .filter((step) => step?.is_open_ended && Number(step?.order) < order)
    .map((step) => sanitizeVariableName(step?.variable_name))
    .filter((name) => {
      if (!name || seen.has(name)) return false;
      seen.add(name);
      return true;
    })
    .map((name) => ({ value: name, label: name }));
}

export function validateGoogleSheetStep(step) {
  if (!isGoogleSheetStep(step)) return { ok: true };
  if (!String(step.google_sheet_spreadsheet_id || '').trim()) {
    return { ok: false, error: 'Select a Google Sheet.' };
  }
  if (!String(step.google_sheet_lookup_column || '').trim()) {
    return { ok: false, error: 'Choose the column to match.' };
  }
  if (!String(step.google_sheet_reply_template || '').trim()) {
    return { ok: false, error: 'Enter the reply template.' };
  }
  if (
    step.google_sheet_lookup_source === 'variable' &&
    !String(step.google_sheet_lookup_variable || '').trim()
  ) {
    return { ok: false, error: 'Choose the variable to look up.' };
  }
  return { ok: true };
}
